import type { Capability } from './capabilities';
import { can } from './capabilities';
import { PERTENTO_COMPANY_ID, USER_ROLES } from './index';

/**
 * Agent tokens (PER-328): personal access tokens an agent, such as the MCP server's client,
 * sends instead of a signed-in user's JWT. They live here, not in the server, because the
 * dashboard asks the same questions when it offers scopes, and the two must not disagree.
 */

/**
 * What a token may do, on top of what its user may do. `read` covers every GET a token is
 * allowed; each other scope names one kind of write. A token holds the intersection of its
 * scopes and its user's capabilities, re-read on every request.
 */
export const AGENT_TOKEN_SCOPES = [
  'read',
  'experiments:write',
  'experiments:lifecycle',
  'experiments:deploy',
  'surveys:write',
  'surveys:lifecycle',
  'websites:publish',
] as const;

export type AgentTokenScope = (typeof AGENT_TOKEN_SCOPES)[number];

/** `pat` is a personal access token made in Settings. `oauth` is for the authorization server (PER-334). */
export const AGENT_TOKEN_KINDS = ['pat', 'oauth'] as const;

export type AgentTokenKind = (typeof AGENT_TOKEN_KINDS)[number];

/**
 * The capability each write scope needs. `read` needs none: anyone signed in may read what
 * their role lets them reach. This is the only place a scope meets a role, through `can()`,
 * so a Member (who holds no write capability) can only ever hold `read`.
 */
const SCOPE_CAPABILITY: Record<Exclude<AgentTokenScope, 'read'>, Capability> = {
  'experiments:write': 'experiments:create',
  'experiments:lifecycle': 'experiments:start',
  'experiments:deploy': 'experiments:deploy',
  'surveys:write': 'surveys:manage',
  'surveys:lifecycle': 'surveys:manage',
  'websites:publish': 'websites:manage',
};

type ScopeHolder = { role?: string | null } | null | undefined;

export const isAgentTokenScope = (value: unknown): value is AgentTokenScope =>
  typeof value === 'string' && (AGENT_TOKEN_SCOPES as readonly string[]).includes(value);

/** Whether this user's role may hold the scope at all. Asked at creation and again on every request. */
export const scopeAllowedFor = (user: ScopeHolder, scope: AgentTokenScope): boolean => {
  if (!user?.role) return false;
  // Reading is every known role's: an unknown role gets nothing, as can() and the method rule decide.
  if (scope === 'read') return (USER_ROLES as readonly string[]).includes(user.role);
  return can(user, SCOPE_CAPABILITY[scope]);
};

/** The scopes this user may put on a token, in display order. */
export const allowedScopesFor = (user: ScopeHolder): AgentTokenScope[] =>
  AGENT_TOKEN_SCOPES.filter((scope) => scopeAllowedFor(user, scope));

/** How long a new token lasts, in days. 90 is the default. */
export const AGENT_TOKEN_EXPIRY_DAYS = [7, 30, 90, 180, 365] as const;

export type AgentTokenExpiryDays = (typeof AGENT_TOKEN_EXPIRY_DAYS)[number];

export const DEFAULT_AGENT_TOKEN_EXPIRY_DAYS: AgentTokenExpiryDays = 90;

export const isAgentTokenExpiryDays = (value: unknown): value is AgentTokenExpiryDays =>
  typeof value === 'number' && (AGENT_TOKEN_EXPIRY_DAYS as readonly number[]).includes(value);

/** Every secret starts with this, so a leaked one is recognisable (and scannable) at a glance. */
export const AGENT_TOKEN_PREFIX = 'ptk_';

/** The prefix followed by base64url of 32 random bytes (43 characters, unpadded). */
export const AGENT_TOKEN_PATTERN = /^ptk_[A-Za-z0-9_-]{43}$/;

/**
 * How many leading characters are stored and shown, so a list can tell tokens apart: the prefix
 * plus 8 random characters (48 bits of a 256-bit secret, which leaves the rest unguessable).
 */
export const AGENT_TOKEN_DISPLAY_LENGTH = 12;

export const MAX_AGENT_TOKEN_NAME_LENGTH = 64;

/** Active (unrevoked, unexpired) tokens per user. Enough for a few machines and a rotation in flight. */
export const MAX_ACTIVE_AGENT_TOKENS = 20;

/**
 * The most websites or companies one token may name. It bounds a request, not access: every id must
 * also be one the creator reaches. Staff can select every website they work on, so it sits well above
 * any list the picker shows, and below what one query can bind (65,535 parameters). A token at this
 * size costs about 80 KB in its row, read on each of its requests.
 */
export const MAX_AGENT_TOKEN_LIMIT_IDS = 10_000;

/**
 * Pertento staff and Super Admins reach every customer website, so one leaked write token of theirs
 * could change every customer site. Theirs must name the websites or companies it reaches whenever
 * it can write. Read-only tokens are left as anyone's. Expiry is not part of the rule: staff get the
 * same choices as everyone (decided 2026-10-29, which dropped a 30-day cap).
 */
type CreatorHolder = { role?: string | null; companyId?: number | null } | null | undefined;

export const isStaffCreator = (user: CreatorHolder): boolean =>
  user?.role === 'Super Admin' || user?.companyId === PERTENTO_COMPANY_ID;

type StaffRefusalParams = {
  user: CreatorHolder;
  scopes: readonly AgentTokenScope[];
  websiteIds: readonly number[] | null;
  companyIds: readonly number[] | null;
  /** Not read: kept so callers that pass it keep compiling. Expiry is not part of the staff rule. */
  expiresInDays?: number;
};

export type StaffTokenRefusal = 'needs-limit' | null;

/** Whether this token would be a staff token that can make changes, which must then name its sites. */
export const isStaffWriteToken = ({ user, scopes }: Pick<StaffRefusalParams, 'user' | 'scopes'>): boolean =>
  isStaffCreator(user) && scopes.some((scope) => scope !== 'read');

/** Why a staff member's token may not be made as asked, or null. The server and the form ask the same. */
export function staffTokenRefusal({ user, scopes, websiteIds, companyIds }: StaffRefusalParams): StaffTokenRefusal {
  if (!isStaffWriteToken({ user, scopes })) return null;
  return websiteIds === null && companyIds === null ? 'needs-limit' : null;
}

export const STAFF_TOKEN_REFUSAL_MESSAGES: Record<Exclude<StaffTokenRefusal, null>, string> = {
  'needs-limit': 'A staff token that can make changes must name the websites or accounts it reaches.',
};

/** What a passkey step-up inside a session is for; each purpose's challenge is consumed only by it. */
export const PASSKEY_STEP_UP_PURPOSES = ['agent-token'] as const;

export type PasskeyStepUpPurpose = (typeof PASSKEY_STEP_UP_PURPOSES)[number];

// OAuth (PER-334)

/**
 * The two kinds of token the OAuth authorization server issues under one connection (an `oauth`
 * agent token row): an access token, good for an hour at /mcp, and a refresh token, rotated on use.
 */
export const OAUTH_TOKEN_KINDS = ['access', 'refresh'] as const;

export type OAuthTokenKind = (typeof OAUTH_TOKEN_KINDS)[number];
